import {
  convertDefaultValueToCellData,
  DossDataFormat,
  isFormulaType,
  isLookupCellData,
  isOverwritableLookupColumnType,
  LookupCellData,
} from '@doss/common-all'
import { isEmpty, isNil } from 'lodash'
import * as Yup from 'yup'
import { CellValue } from '~/components/records/add-record/AddRecordDialog'
import { RecordDetailTableCell } from '../../components/records/types'
import {
  CreateOrUpdateTableCellInput,
  DataType,
  IndexType,
  TableColumn,
  UpdatedTableRowDetailsSubscription,
} from '../generated/apollo-operations'
import { getRecordInitialValues } from './formik'
import { getRowName } from './table'
import { hasCellDataChanged } from './table-cell'

/**
 * Helper function to get the validation schema for a table column
 */
export function getTableColumnValidationSchema(
  column: Pick<
    TableColumn,
    'name' | 'innerType' | 'isNullable' | 'type' | 'indexType' | 'enumOptions' | 'indexAutogenerate'
  > & { lookupColumn: { id: string; type: DataType } | null },
  { allowOverwrittenLookup = true }: { allowOverwrittenLookup?: boolean } = {},
): Yup.AnySchema {
  const { name, isNullable, type, indexAutogenerate, innerType, lookupColumn } = column

  let validator: Yup.AnySchema = Yup.mixed()

  switch (type) {
    case DataType.BOOLEAN:
      validator = Yup.boolean().typeError(`${name} must be a boolean`).default(false).label(name)
      break
    case DataType.STRING:
      validator = Yup.string().typeError(`${name} must be a string`).label(name)
      break
    case DataType.NUMBER:
      validator = Yup.number().typeError(`${name} must be a number`).label(name)
      break
    case DataType.DATE:
    case DataType.DATE_TIME:
      validator = Yup.date().typeError(`${name} must be a date`).label(name)
      break
    case DataType.ENUM:
      if (isNil(column.enumOptions)) {
        throw new Error(`Column ${name} is an enum but has no enum options`)
      }
      validator = Yup.string().oneOf(column.enumOptions).label(name)
      break
    case DataType.FILE_URL:
      validator = Yup.string().label(name)
      break
    case DataType.USER:
      validator = Yup.mixed().label(name)
      break
    case DataType.ID:
      switch (column.indexType) {
        case IndexType.NONE:
          throw new Error(`Column ${name} is an ID but has no index type`)
        case IndexType.NUMBER:
          validator = Yup.number()
            .positive()
            .integer()
            .typeError(`${name} must be a positive integer`)
            .label(name)

          break
        case IndexType.STRING:
          validator = Yup.string().typeError(`${name} must be a string`).label(name)
          break
      }
      validator = Yup.string().label(name)
      break
    case DataType.LOOKUP:
      validator = Yup.mixed()
        .test({
          name: 'is-valid-lookup',
          message({ value }) {
            if (isNil(value)) {
              return `${name} is required`
            }
            if (Array.isArray(value)) {
              return `${name} contains invalid lookup value(s)`
            }
            if (!lookupColumn) {
              return `${name} requires a valid lookup column`
            }
            // If overwriting the lookup, check if it is an overwritable lookup column. If not, return
            // no error message
            if (isLookupCellData(value)) {
              if (
                !allowOverwrittenLookup ||
                (value.isOverwritten && !isOverwritableLookupColumnType(lookupColumn.type))
              ) {
                return `${name} cannot be detached from lookup`
              }
              return null
            }
            if (lookupColumn.type === DataType.NUMBER) {
              return `Detached value for ${name} must be a number`
            }
            return `${name} contains an invalid value`
          },
          test(value) {
            if (isNil(value)) {
              return isNullable
            }
            if (Array.isArray(value)) {
              // Check if it's an array of LookupCellValue
              return value.every((item) =>
                Yup.object()
                  .shape({
                    id: Yup.string().required(),
                    userVisibleValue: Yup.string().nullable(),
                    row: Yup.object().shape({ id: Yup.string().required() }).required(),
                    data: Yup.mixed().nullable(),
                  })
                  .isValidSync(item),
              )
            } else {
              if (
                !allowOverwrittenLookup ||
                !lookupColumn ||
                !isOverwritableLookupColumnType(lookupColumn.type)
              ) {
                return false
              }
              // Check if it's a single LookupCellData. If so, is overwritten must be true
              if (lookupColumn.type === DataType.NUMBER) {
                return Yup.object()
                  .shape({
                    type: Yup.string().oneOf([DossDataFormat.LOOKUP]).required(),
                    data: Yup.number().nullable().defined(),
                    // isOverwritten must be true in this case
                    isOverwritten: Yup.boolean().required().isTrue(),
                  })
                  .isValidSync(value)
              }

              return Yup.object()
                .shape({
                  type: Yup.string().oneOf([DossDataFormat.LOOKUP]).required(),
                  data: Yup.string().nullable().defined(),
                  // isOverwritten must be true in this case
                  isOverwritten: Yup.boolean().required().isTrue(),
                })
                .isValidSync(value)
            }
          },
        })
        .label(name)

      break
    case DataType.ARRAY: {
      let arrayValidator: Yup.ArraySchema<unknown[] | undefined, Yup.AnyObject, ''>
      if (innerType === DataType.LOOKUP) {
        arrayValidator = Yup.array()
          .of(Yup.mixed())
          .typeError(`${name} must be an array of lookup cells`)
      } else if (innerType === DataType.USER) {
        arrayValidator = Yup.array().of(Yup.mixed()).typeError(`${name} must be an array of users`)
      } else {
        arrayValidator = Yup.array()
          .of(Yup.string())
          .typeError(`${name} must be an array of strings`)
      }
      if (!isNullable) {
        arrayValidator = arrayValidator.min(1, `${name} must have at least one value`)
      }
      validator = arrayValidator.label(name)
      break
    }
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
      validator = Yup.mixed().label(name)
      break
    case DataType.FORMULA_BOOLEAN:
      // FORMULA_BOOLEAN is out of scope for the non-numeric formulas project
      return Yup.mixed()
  }

  if (
    !isNullable &&
    !indexAutogenerate &&
    type !== DataType.FORMULA_NUMBER &&
    type !== DataType.FORMULA_STRING &&
    type !== DataType.FORMULA_DATE &&
    type !== DataType.FORMULA_DATE_TIME
  ) {
    // Formulas are required cells that don't have user-input values
    validator = validator.test({
      name: 'is-required',
      message: `${name} is required`,
      test(value) {
        // If the lookup field is required, check that the array of lookup cell values has
        // at least one value. If overwriting the lookup, check that the data value is not
        // empty
        if (type === DataType.LOOKUP) {
          if (Array.isArray(value)) {
            return !isEmpty(value)
          } else if (value && typeof value === 'object') {
            return !isNil((value as LookupCellData).data) && (value as LookupCellData).data !== ''
          }
          return false
        }
        return !isNil(value)
      },
    })
  } else {
    validator = validator.nullable()
  }

  return validator
}

// Helper function to get the validation schema for a table row / record
export function getRecordValidationSchema(
  columns: (Pick<
    TableColumn,
    | 'id'
    | 'name'
    | 'innerType'
    | 'isNullable'
    | 'type'
    | 'indexType'
    | 'enumOptions'
    | 'indexAutogenerate'
  > & { lookupColumn: { id: string; type: DataType } | null })[],
) {
  const validationSchema: Yup.ObjectShape = {}

  for (const column of columns) {
    const validator = getTableColumnValidationSchema(column)

    validationSchema[column.id] = validator
  }

  return Yup.object().shape(validationSchema)
}

/**
 * Converts add-record form values into the cells for `createTableRow`.
 *
 * Skips formulas and columns the user can't edit. The server fills both,
 * and sending a non-editable column's empty default would fail its edit-cell check.
 */
export function getCreateTableRowCells(
  values: Record<string, CellValue>,
  columns: Pick<TableColumn, 'id' | 'type' | 'innerType' | 'indexType'>[],
  canEditCell: (columnId: string) => boolean,
): CreateOrUpdateTableCellInput[] {
  return Object.entries(values).flatMap(([columnId, value]) => {
    const column = columns.find((column) => column.id === columnId)

    if (!column) {
      throw new Error(`No column found for column ${columnId}`)
    }

    if (isFormulaType(column.type) || !canEditCell(column.id)) {
      return []
    }

    const { data, lookupCellIds, documentIds, userIds } = convertDefaultValueToCellData(
      value,
      column.type,
      column.innerType,
      column.indexType,
    )

    return [{ tableColumnId: columnId, data, lookupCellIds, documentIds, userIds }]
  })
}

/**
 * When the table row update subscription comes in, we need to update the formik data with the new
 * values. However, we shouldn't update dirty fields because the user is currently editing them.
 *
 * A cell is dirty when its current form value differs from the server cell data, so a field counts
 * as dirty the moment its value diverges — we do not wait for a blur to set formik.touched. This is
 * what keeps an incoming row update (e.g. a parent-row recompute triggered by saving a child row)
 * from overwriting an in-flight edit.
 */
export function handleUpdatedTableRowDetails(
  formikData: Record<string, CellValue>,
  updatedTableRow: UpdatedTableRowDetailsSubscription['updatedTableRowDetails'],
  rowData: { cells: RecordDetailTableCell[] },
): Record<string, CellValue> {
  const omitDirtyCells = rowData.cells.filter((cell) =>
    hasCellDataChanged(
      cell.column,
      cell.data,
      formikData[cell.column.id],
      cell.lookupCells.map((lookupCell) => lookupCell.id),
      cell.documents.map((document) => document.id),
      cell.users.map((user) => user.id),
    ),
  )

  const initialValues = getRecordInitialValues({
    ...updatedTableRow,
    name: getRowName(updatedTableRow.cells) ?? '',
  })
  const currentValues = formikData
  const updatedValues = Object.keys(initialValues).reduce(
    (acc, key) => {
      if (omitDirtyCells.some((cell) => cell.column.id === key)) {
        acc[key] = currentValues[key]
      } else {
        acc[key] = initialValues[key]
      }
      return acc
    },
    {} as Record<string, CellValue>,
  )

  return updatedValues
}
